import { useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import "./RowActions.css";

/**
 * Row actions that adapt to screen size.
 *
 * action: { key, label, icon, onClick, color?, disabled?, hidden?, danger? }
 *  inline    – shown as text buttons (or inside the menu when `collapsed`)
 *  menu      – always inside the three-dot menu
 *  collapsed – put everything in the three-dot menu (use on mobile)
 *
 * The menu is rendered in a portal, so the table's scroll container never clips it.
 */
const visible = (items) => items.filter((item) => !item.hidden);

const RowActions = ({ inline = [], menu = [], collapsed = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const inlineItems = visible(inline);
  const menuItems = visible(menu);

  const items = collapsed
    ? [
        ...inlineItems,
        ...(inlineItems.length && menuItems.length
          ? [{ key: "divider", divider: true }]
          : []),
        ...menuItems,
      ]
    : menuItems;

  const close = () => setAnchorEl(null);
  const run = (item) => {
    close();
    item.onClick?.();
  };

  return (
    <div className="row-actions" onClick={(e) => e.stopPropagation()}>
      {!collapsed &&
        inlineItems.map((item) => (
          <Button
            key={item.key}
            size="small"
            variant="text"
            color={item.color || "neutral"}
            disabled={item.disabled}
            startIcon={
              <span className="material-symbols-outlined">{item.icon}</span>
            }
            onClick={item.onClick}
          >
            {item.label}
          </Button>
        ))}

      {items.length > 0 && (
        <>
          <IconButton
            size="small"
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <span className="material-symbols-outlined">more_vert</span>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={close}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {items.map((item) =>
              item.divider ? (
                <Divider key={item.key} />
              ) : (
                <MenuItem
                  key={item.key}
                  disabled={item.disabled}
                  onClick={() => run(item)}
                  className={`row-actions__item${item.danger ? " row-actions__item--danger" : ""}`}
                >
                  <ListItemIcon>
                    <span className="material-symbols-outlined">
                      {item.icon}
                    </span>
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </MenuItem>
              ),
            )}
          </Menu>
        </>
      )}
    </div>
  );
};

export default RowActions;
